/**
 * Broker Data Pipeline → Quarantine notes (ADR-047 §3.6).
 *
 * What the writers refused, with the reason, the signals and where the writer finds the observation again; never the
 * document, the rate or the lane. Notes live 21 days and are replayed by their writer as its ADR declares; a note never
 * becomes a record. Filtered by writer and reason; one note opens whole.
 */

import Alert from '@mui/joy/Alert'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import CircularProgress from '@mui/joy/CircularProgress'
import Modal from '@mui/joy/Modal'
import ModalClose from '@mui/joy/ModalClose'
import ModalDialog from '@mui/joy/ModalDialog'
import Option from '@mui/joy/Option'
import Select from '@mui/joy/Select'
import Sheet from '@mui/joy/Sheet'
import Table from '@mui/joy/Table'
import Typography from '@mui/joy/Typography'
import type { BrokerPipelineQuarantineDetail } from '@numeo/types-core'
import * as React from 'react'
import { useSearchParams } from 'react-router-dom'
import { brokerPipelineApi } from 'api/brokerPipeline'
import { ErrorState, Loading, WindowPicker, formatNumber, useLoadPipelineData } from '../load-pipeline/components/shared'
import { writerTitle } from './components/shared'

function NoteDialog({ id, writer, onClose }: { id: string; writer: string; onClose: () => void }) {
    const { data, error } = useLoadPipelineData<BrokerPipelineQuarantineDetail>(() => brokerPipelineApi.getQuarantineNote(id, writer), [id, writer])
    const field = (label: string, value: React.ReactNode) => (
        <>
            <Typography level="body-xs" textColor="text.tertiary">
                {label}
            </Typography>
            <Typography level="body-sm" sx={{ wordBreak: 'break-all' }}>
                {value ?? '—'}
            </Typography>
        </>
    )
    return (
        <Modal open onClose={onClose}>
            <ModalDialog sx={{ width: 'min(760px, 92vw)', maxHeight: '88vh', overflow: 'auto' }}>
                <ModalClose />
                <Typography level="title-lg">Quarantine note</Typography>
                {error ? <ErrorState error={error} /> : null}
                {!data && !error ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : null}
                {data ? (
                    <>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 0.5, columnGap: 2, mb: 2 }}>
                            {field('Writer', writerTitle(data.writer))}
                            {field('Reason', data.reason)}
                            {field('Detail', data.detail)}
                            {field('MC', data.mc)}
                            {field('Source', data.sourceRef)}
                            {field('Noted', data.quarantinedAt ? new Date(data.quarantinedAt).toLocaleString() : undefined)}
                        </Box>
                        <Typography level="title-sm" sx={{ mb: 0.5 }}>
                            Signals
                        </Typography>
                        {data.signals && Object.keys(data.signals).length > 0 ? (
                            <Box component="pre" sx={{ fontSize: 11, m: 0, p: 1.5, borderRadius: 'sm', bgcolor: 'background.level1', overflow: 'auto' }}>
                                {JSON.stringify(data.signals, null, 2)}
                            </Box>
                        ) : (
                            <Typography level="body-sm">The writer kept no signals for this refusal: the reason and detail are the whole of it.</Typography>
                        )}
                    </>
                ) : null}
            </ModalDialog>
        </Modal>
    )
}

export default function BrokerPipelineQuarantinePage() {
    const [params, setParams] = useSearchParams()
    const writer = params.get('writer') ?? ''
    const reason = params.get('reason') ?? ''
    const [hours, setHours] = React.useState(168)
    const [open, setOpen] = React.useState<{ id: string; writer: string } | null>(null)
    const writers = useLoadPipelineData(() => brokerPipelineApi.getWriters(), [])
    const { data, error, loading } = useLoadPipelineData(
        () => brokerPipelineApi.getQuarantine(hours, writer || undefined, reason || undefined),
        [hours, writer, reason]
    )

    const setFilter = (name: 'writer' | 'reason', value: string) => {
        const next = new URLSearchParams(params)
        if (value) next.set(name, value)
        else next.delete(name)
        // A reason belongs to its writer: a new writer clears it.
        if (name === 'writer') next.delete('reason')
        setParams(next, { replace: true })
    }
    const reasons = [...new Set((data?.counts ?? []).map((c) => c.reason))].sort()
    const notBuilt = writers.data?.find((w) => w.writer === writer && !w.built)

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1, flexWrap: 'wrap' }}>
                <Typography level="h2">Quarantine notes</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Select size="sm" value={writer} onChange={(_, value) => setFilter('writer', value ?? '')} sx={{ minWidth: 160 }}>
                        <Option value="">All writers</Option>
                        {(writers.data ?? []).map((w) => (
                            <Option key={w.writer} value={w.writer}>
                                {writerTitle(w.writer)}
                                {w.built ? '' : ' (not yet built)'}
                            </Option>
                        ))}
                    </Select>
                    <Select size="sm" value={reason} onChange={(_, value) => setFilter('reason', value ?? '')} sx={{ minWidth: 190 }}>
                        <Option value="">All reasons</Option>
                        {reasons.map((r) => (
                            <Option key={r} value={r}>
                                {r}
                            </Option>
                        ))}
                    </Select>
                    <WindowPicker hours={hours} onChange={setHours} maxHours={168} />
                </Box>
            </Box>
            <Typography level="body-sm" textColor="text.tertiary" sx={{ mb: 3 }}>
                A writer that refuses an observation notes the reason, the signals and where it can find the observation again; never the document, the rate or
                the lane. Notes expire after 21 days and are replayed by their writer (ADR-047 §3.6). At most 500 notes are read per writer and window.
            </Typography>

            {notBuilt ? (
                <Alert color="neutral" variant="soft" sx={{ mb: 2 }}>
                    {writerTitle(notBuilt.writer)} is not yet built: it has no notes to show.
                </Alert>
            ) : null}
            {error ? <ErrorState error={error} /> : null}
            {loading && !data ? <Loading /> : null}
            {data?.unavailable.length ? (
                <Alert color="warning" variant="soft" sx={{ mb: 2 }}>
                    Could not read the notes of {data.unavailable.map(writerTitle).join(', ')}. They are missing below, not absent.
                </Alert>
            ) : null}
            {data?.capped.length ? (
                <Alert color="warning" variant="soft" sx={{ mb: 2 }}>
                    {data.capped.map(writerTitle).join(', ')} reached the 500-note cap: their counts are a floor. Narrow the window to see the rest.
                </Alert>
            ) : null}

            {data && !notBuilt ? (
                <>
                    {data.counts.length > 0 ? (
                        <Sheet variant="outlined" sx={{ borderRadius: 'sm', p: 2, mb: 3 }}>
                            <Typography level="title-md" sx={{ mb: 1 }}>
                                By writer and reason
                            </Typography>
                            <Table size="sm">
                                <tbody>
                                    {data.counts.map((c) => (
                                        <tr key={`${c.writer}|${c.reason}`}>
                                            <td style={{ width: 140 }}>{writerTitle(c.writer)}</td>
                                            <td>
                                                <Typography level="body-xs" sx={{ fontFamily: 'monospace' }}>
                                                    {c.reason}
                                                </Typography>
                                            </td>
                                            <td style={{ textAlign: 'right' }}>{formatNumber(c.count)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Sheet>
                    ) : null}

                    {data.notes.length === 0 ? (
                        <Alert color="neutral" variant="soft">
                            No notes in this window{reason ? ` for ${reason}` : ''}. A writer notes only what it refuses; before the writers run, there is
                            nothing to refuse.
                        </Alert>
                    ) : (
                        <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
                            <Table size="sm" stripe="odd" hoverRow>
                                <thead>
                                    <tr>
                                        <th style={{ width: 170 }}>Noted</th>
                                        <th style={{ width: 110 }}>Writer</th>
                                        <th style={{ width: 170 }}>Reason</th>
                                        <th style={{ width: 100 }}>MC</th>
                                        <th>Detail</th>
                                        <th style={{ width: 200 }}>Source</th>
                                        <th style={{ width: 80 }} />
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.notes.map((n) => (
                                        <tr key={`${n.writer}/${n.id}`}>
                                            <td>{n.quarantinedAt ? new Date(n.quarantinedAt).toLocaleString() : '—'}</td>
                                            <td>{writerTitle(n.writer)}</td>
                                            <td>
                                                <Typography level="body-xs" sx={{ fontFamily: 'monospace' }}>
                                                    {n.reason}
                                                </Typography>
                                            </td>
                                            <td>{n.mc ?? '—'}</td>
                                            <td>
                                                <Typography level="body-xs">{n.detail ?? '—'}</Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-xs" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                                    {n.sourceRef ?? '—'}
                                                </Typography>
                                            </td>
                                            <td>
                                                <Button size="sm" variant="plain" onClick={() => setOpen({ id: n.id, writer: n.writer })}>
                                                    Open
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Sheet>
                    )}
                </>
            ) : null}

            {open ? <NoteDialog id={open.id} writer={open.writer} onClose={() => setOpen(null)} /> : null}
        </Box>
    )
}
